/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useLoadCaptcha from '@/hooks/useLoadCaptcha';
import { useHTTPError } from 'br2s-ui-common-utils-02';
import {
  Button,
  InlineAlert,
  RadioButtonField,
} from 'br2s-ui-componentlibrary_r2_02';
import { PLEASE_SELECT_MESSAGE } from '../crmConstants';
import { generateSession, modifyQueryParam } from '../crmUtilities/crmUtils';

import './crm-survey-form.scss';
import '../crm-form.scss';
import { CRM_FORM } from '@/services/api/crm/crm';

type Alert = {
  type: 'error' | 'success' | 'informative' | 'warning';
  message: ReactNode;
};

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const CRMSurveyForm = () => {
  useLoadCaptcha();
  const { handleHTTPError } = useHTTPError();

  const isFirstRender = useRef(true);
  const [surveyFormData, setSurveyFormData] = useState<any>({});
  const [formValue, setFormValue] = useState<any>([]);
  const [formError, setFormError] = useState({});
  const [alert, setAlert] = useState<Alert>({
    type: 'informative',
    message: '',
  });
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  useEffect(() => {
    if (isFirstRender.current) {
      fetchSession();     
      isFirstRender.current = false;
      return () => window.sessionStorage.removeItem('transactionId');
    }
  }, []);

  // istanbul ignore next
  const fetchSession = async () => {
    try {
      await generateSession();
      formLoad();
    } catch (error) {
      console.error(error);
      handleHTTPError(error, {
        errorType: 'inline',
      });
    }
  };

  const formLoad = async () => {
    window.sessionStorage.setItem('transactionId', uuidv4());
    const encryptedFormParam = modifyQueryParam();
    try {
      const response = await CRM_FORM.getSurveyForm(encryptedFormParam);
      if (response.status === 200) {
        setSurveyFormData(response.data.result);
        setIsSubmitDisabled(false);
      }
    } catch (error: any) {
      console.log(error);
      handleHTTPError(error, {
        errorType: 'inline',
      });
      setIsSubmitDisabled(false);
    }
  };

  const removeError = (key: string) => {
    setFormError((prevState) => {
      const temp = { ...prevState };
      delete (temp as any)[key];
      return temp;
    });
  };

  const handleSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    setIsSubmitDisabled(true);
    // @ts-ignore
    window.grecaptcha.ready(() => {
      // @ts-ignore
      window.grecaptcha
        .execute(RECAPTCHA_SITE_KEY, { action: 'submit' })
        .then(async (captchaToken: string) => {
          try {
            const payload = {
              requestTransactId:
                window.sessionStorage.getItem('transactionId') || '',
              webformParameter: modifyQueryParam(),
              surveyAnswerList: Object.entries(formValue).map((answer) => ({
                '@odata.type': '#Microsoft.Dynamics.CRM.acra_surveyanswer',
                'acra_SurveyAnswerSurveyQuestionLookup@odata.bind': `/acra_surveyquestions(${answer[0]})`,
                acra_surveyanswersurveyanswernumber: answer[1],
              })),
            };
            const response = await CRM_FORM.submitSurveyForm(
              payload,
              captchaToken
            );

            if (response.status === 200) {
              setAlert({
                type: 'success',
                message: 'Success: Your survey has been submitted.',
              });
              resetSurveyForm();
            }
          } catch (error: any) {
            console.error(error);
            handleHTTPError(error, {
              errorType: 'inline',
            });
            setIsSubmitDisabled(false);
          }
        });
    });
  };

  const setFormEmptyError = () => {
    if (!alert.message) {
      setAlert({
        type: 'error',
        message: 'Please fill in the required fields',
      });
    }
  };

  const validateSurvey = (key: string, val: string) => {
    let isValid = true;
    if (val === undefined || val === '') {
      setFormError((prevState) => ({
        ...prevState,
        [key]: 'required',
      }));
      setFormEmptyError();
      isValid = false;
    } else {
      removeError(key);
    }
    return isValid;
  };

  const handleInputChange = (key: string, val: string) => {
    setFormValue((prevState: any) => ({ ...prevState, [key]: val }));
    validateSurvey(key, val);
  };

  const validateForm = () => {
    let isValid = true;
    surveyFormData?.surveyQuestions?.forEach((item: any) => {
      const key = item.questionGuid;
      if (!validateSurvey(key, formValue[key])) {
        isValid = false;
      }
    });
    if (isValid) {
      if (alert.message) {
        setAlert({
          type: 'informative',
          message: '',
        });
      }
    }
    return isValid;
  };

  const resetSurveyForm = () => {
    setFormValue({});
    setFormError({});
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    window.sessionStorage.setItem('transactionId', uuidv4());
  };

  return (
    <div className="crm-form-wrapper">
      <div className="container crm-form">
        <div className="row">
          <div className="col-12 col-md-6 g-0">
            {alert.message && (
              <InlineAlert
                type={alert.type}
                // @ts-ignore
                headline={alert.message}
              />
            )}
          </div>
        </div>
        <div className="crm-survey-form__radio">
          {surveyFormData?.surveyQuestions?.map((item: any) => {
            const key = item.questionGuid;
            const options = item.surveyQuestionOptions.map((option: any) => ({
              id: `${key}-${option.value}`,
              label: option.label,
              value: option.value,
            }));

            return (
              <div key={key} className="row">
                <div className="col-12 col-md-6 g-0">
                  <RadioButtonField
                    id="radio"
                    label={item.question}
                    options={options}
                    radioButtonValue={formValue[key]}
                    onChange={(e: any) => handleInputChange(key, e)}
                    state="error"
                    helperText={
                      (formError as any)[key] === 'required'
                        ? PLEASE_SELECT_MESSAGE
                        : ''
                    }
                  />
                </div>
              </div>
            );
          })}
          <div className="row">
            <div className="col-12 col-md-6 g-0">
              <Button
                id="submit"
                label="Submit"
                onClick={handleSubmit}
                isBlock
                isDisabled={isSubmitDisabled}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default CRMSurveyForm;
